import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Typography,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Label as LabelIcon,
} from "@mui/icons-material";
import TagViewModel from "../viewmodels/TagViewModel";
import CreateTagDialog from "../components/CreateTagDialog";
import TagService from "../services/TagService";

const TagListView = () => {
  const [tagViewModel] = useState(() => new TagViewModel());
  const [state, setState] = useState({
    tags: [],
    filteredTags: [],
    searchQuery: "",
    selectedTags: [],
    isLoading: false,
    error: null,
  });
  const [tags, setTags] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  useEffect(() => {
    const updateState = () => {
      setState(tagViewModel.state);
    };

    updateState();
    tagViewModel.onStateChange = updateState;

    return () => {
      tagViewModel.onStateChange = null;
    };
  }, [tagViewModel]);
  function fetch() {
    TagService.getAllTags().then((res) => setTags(res));
  }
  useEffect(() => {
    fetch();
  }, []);

  const handleSearchChange = (event) => {
    tagViewModel.searchTags(event.target.value);
  };

  const handleSelectTag = (tagId) => {
    tagViewModel.selectTag(tagId);
  };

  const handleSelectAll = () => {
    if ((state.selectedTags?.length || 0) === (state.filteredTags?.length || 0)) {
      tagViewModel.deselectAllTags();
    } else {
      tagViewModel.selectAllTags();
    }
  };

  const handleDeleteTag = (tag) => {
    setTagToDelete(tag);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTag = () => {
    if (tagToDelete) {
      tagViewModel.deleteTag(tagToDelete.id);
    }
    setDeleteDialogOpen(false);
    setTagToDelete(null);
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setIsEditDialogOpen(true);
  };

  const handleCreateTag = () => {
    setIsCreateDialogOpen(true);
  };

  const handleTagCreated = (newTag) => {
    fetch();
    setIsCreateDialogOpen(false);
  };

  const handleTagUpdated = (updatedTag) => {
    fetch();
    setIsEditDialogOpen(false);
    setEditingTag(null);
  };

  const getColorName = (color) => {
    const colorMap = {
      "#f44336": "Red",
      "#e91e63": "Pink",
      "#9c27b0": "Purple",
      "#673ab7": "Deep Purple",
      "#3f51b5": "Indigo",
      "#2196f3": "Blue",
      "#03a9f4": "Light Blue",
      "#00bcd4": "Cyan",
      "#009688": "Teal",
      "#4caf50": "Green",
      "#8bc34a": "Light Green",
      "#cddc39": "Lime",
      "#ffeb3b": "Yellow",
      "#ffc107": "Amber",
      "#ff9800": "Orange",
      "#ff5722": "Deep Orange",
      "#795548": "Brown",
      "#9e9e9e": "Grey",
      "#607d8b": "Blue Grey",
    };
    return colorMap[color] || "Custom";
  };

  if (state.isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          pb: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <LabelIcon sx={{ color: "#1976d2", fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#1976d2" }}>
            Tag Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTag}
          sx={{
            backgroundColor: "#1976d2",
            "&:hover": { backgroundColor: "#1565c0" },
            borderRadius: 2,
            px: 3,
            py: 1,
          }}
        >
          Add Tag
        </Button>
      </Box>

      {/* Search and Stats */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            placeholder="Search tags..."
            value={state.searchQuery || ""}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "#666", mr: 1 }} />,
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#f8f9fa",
              },
            }}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            {(state.filteredTags?.length || 0)} of {(state.tags?.length || 0)} tags
          </Typography>
        </Box>
        {(state.selectedTags?.length || 0) > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {state.selectedTags.length} selected
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete Selected
            </Button>
          </Box>
        )}
      </Box>

      {/* Error Alert */}
      {state.error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={tagViewModel.clearError}
        >
          {state.error}
        </Alert>
      )}

      {/* Tags Table */}
      <Paper
        sx={{
          flex: 1,
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderRadius: 2,
        }}
      >
        <TableContainer sx={{ height: "100%" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      (state.filteredTags?.length || 0) > 0 &&
                      (state.selectedTags?.length || 0) === (state.filteredTags?.length || 0)
                    }
                    indeterminate={
                      (state.selectedTags?.length || 0) > 0 &&
                      (state.selectedTags?.length || 0) < (state.filteredTags?.length || 0)
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tag Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Color</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Contacts</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tags?.map((tag) => (
                <TableRow
                  key={tag.getId()}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "#f8f9fa" },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={(state.selectedTags || []).includes(tag.getId())}
                      onChange={() => handleSelectTag(tag.getId())}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={tag.getName()}
                        size="small"
                        sx={{
                          backgroundColor: tag.getColor(),
                          color: "white",
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: tag.getColor(),
                          border: "2px solid #e0e0e0",
                        }}
                      />
                      <Typography variant="body2">
                        {getColorName(tag.getColor())}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {tag.getDescription() || "No description"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tag.getContactsCount()}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(tag.getCreatedAt()).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="Edit Tag">
                        <IconButton
                          size="small"
                          onClick={() => handleEditTag(tag)}
                          sx={{ color: "#1976d2" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Tag">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTag(tag)}
                          sx={{ color: "#f44336" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Tag Dialog */}
      <CreateTagDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onTagCreated={handleTagCreated}
        tagViewModel={tagViewModel}
      />

      {/* Edit Tag Dialog */}
      {editingTag && (
        <CreateTagDialog
          open={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          onTagCreated={handleTagUpdated}
          tagViewModel={tagViewModel}
          editingTag={editingTag}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            {tagToDelete
              ? `Are you sure you want to delete the tag "${tagToDelete.name}"?`
              : `Are you sure you want to delete ${state.selectedTags?.length || 0} selected tags?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteTag} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagListView;
